import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client';
import { ApolloClient, createHttpLink, gql, InMemoryCache } from '@apollo/client/core';
import type { ApolloLink } from '@apollo/client/link';
import { Observable, from, switchMap, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { readStoredToken } from '../auth/auth-token';

const REFRESH_TOKEN_MUTATION = gql`
  mutation refreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      tokens {
        access { token }
        refresh { token }
      }
    }
  }
`;

interface RefreshTokenData {
  refreshToken: {
    tokens: {
      access: { token: string };
      refresh: { token: string };
    };
  };
}

let isRefreshing = false;
let pendingRequests: Array<() => void> = [];

const getItem = (key: string): string | null => {
  const value = localStorage.getItem(key);
  try {
    return JSON.parse(value as string) as string;
  } catch {
    return null;
  }
};

const setItem = (key: string, value: string): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

const resolvePendingRequests = (): void => {
  pendingRequests.forEach(cb => cb());
  pendingRequests = [];
};

const getNewToken = async (): Promise<void> => {
  const refreshToken = getItem('refresh_token');
  const renewClient = new ApolloClient({
    link: createHttpLink({ uri: environment.graphqlURI }),
    cache: new InMemoryCache(),
  });
  // const result = await renewClient.mutate<RefreshTokenData>({
  //   mutation: REFRESH_TOKEN_MUTATION,
  //   variables: { refreshToken },
  // });
  // setItem('access_token', result.data!.refreshToken.tokens.access.token);
  // setItem('refresh_token', result.data!.refreshToken.tokens.refresh.token);
};

const ACCESS_DENIED_MSG = 'User does not has access to this location';
let navigateToDashboard: (() => void) | null = null;
let isRedirectingToDashboard = false;

export function setDashboardNavigator(fn: () => void): void {
  navigateToDashboard = fn;
}

// Returns EMPTY so Apollo stops retrying and completes the observable
function handleAccessDenied(): Observable<ApolloLink.Result> {
  if (!isRedirectingToDashboard) {
    isRedirectingToDashboard = true;
    // Use setTimeout to ensure navigation runs inside Angular zone
    setTimeout(() => {
      if (navigateToDashboard) {
        navigateToDashboard();
      } else {
        window.location.href = '/dashboard';
      }
      isRedirectingToDashboard = false;
    }, 0);
  }
  return EMPTY as unknown as Observable<ApolloLink.Result>;
}

function isAccessDeniedNetworkError(error: unknown): boolean {
  const httpErr = error as { status?: number; error?: unknown };
  if (httpErr.status !== 403) return false;

  const raw = httpErr.error;
  let body: Record<string, unknown> | null = null;

  if (raw && typeof raw === 'object') {
    body = raw as Record<string, unknown>;
  } else if (typeof raw === 'string') {
    try { body = JSON.parse(raw) as Record<string, unknown>; } catch { /* ignore */ }
  }

  return body?.['success'] === false && body?.['message'] === ACCESS_DENIED_MSG;
}

const errorLink = new ErrorLink(({ error, operation, forward }): Observable<ApolloLink.Result> | void => {
  // ── 1. GraphQL-level errors ──────────────────────────────────────────────
  if (CombinedGraphQLErrors.is(error)) {
    const authError    = error.errors.find(err => err?.message === 'Please authenticate');
    const accessDenied = error.errors.find(err => err?.message === ACCESS_DENIED_MSG);

    if (accessDenied) {
      return handleAccessDenied();
    }

    if (authError) {
      if (operation.getContext()['retried'] as boolean) return EMPTY as unknown as Observable<ApolloLink.Result>;

      if (!isRefreshing) {
        isRefreshing = true;

        return from(
          getNewToken().catch((err) => {
            pendingRequests = [];
            isRefreshing = false;
            throw err;
          })
        ).pipe(
          switchMap(() => {
            const currentHeaders = (operation.getContext().headers as HttpHeaders) ?? new HttpHeaders();
            operation.setContext({
              headers: currentHeaders.set('Authorization', `Bearer ${readStoredToken()}`),
              retried: true,
            });
            resolvePendingRequests();
            isRefreshing = false;
            return forward(operation);
          }),
          catchError(() => EMPTY),
        ) as Observable<ApolloLink.Result>;
      } else {
        return new Observable<ApolloLink.Result>(observer => {
          pendingRequests.push(() => {
            operation.setContext({ retried: true });
            forward(operation).subscribe({
              next: v => observer.next(v),
              error: e => observer.error(e),
              complete: () => observer.complete(),
            });
          });
        });
      }
    }

    error.errors.forEach(err => {
      if (err?.message !== 'Please authenticate' && err?.message !== ACCESS_DENIED_MSG) {
        console.error('[GraphQL error]:', err.message, err.locations, err.path);
      }
    });
    return;
  }

  // ── 2. Network-level errors (HTTP 403 access denied) ────────────────────
  if (error && isAccessDeniedNetworkError(error)) {
    return handleAccessDenied();
  }

  if (error) {
    console.error('[Network error]:', error);
  }
});

export default errorLink;
